import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, ElementRef, EventEmitter, inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificationService } from '../../services/notification.service';
import { InfoTipComponent } from './info-tip.component';

declare const google: any;

export interface SpaceMapLocation {
  latitude: number;
  longitude: number;
  google_place_id?: string | null;
  formatted_address?: string | null;
}

interface GoogleMapsRuntimeConfig {
  apiKey: string;
}

// Corrección pedida: la clave de Google Maps ya no vive hardcodeada en un
// archivo generado dentro de este proyecto (google-maps.generated.ts,
// eliminado) -- vive solo en el .env del backend (AppConfig.googleMaps) y
// se pide aquí, en tiempo real, la primera vez que hace falta. Se cachea a
// nivel de módulo (mismo criterio que ya tenía mapsLoader) para no volver a
// pedirla cada vez que se monta el selector.
//
// Sin Map ID a propósito (pedido del jefe: "ya no se utilizaría el del map
// id", que solo dependa de la API key). Un Map ID solo hace falta para
// Advanced Markers (google.maps.marker.AdvancedMarkerElement) -- el
// selector usa el marcador clásico (google.maps.Marker), que funciona con
// cualquier clave válida de Maps JavaScript API sin necesitar registrar ni
// mantener un Map ID aparte.
let cachedConfig: GoogleMapsRuntimeConfig | null = null;
let mapsLoader: Promise<GoogleMapsRuntimeConfig> | null = null;

function loadGoogleMaps(http: HttpClient): Promise<GoogleMapsRuntimeConfig> {
  if ((window as any).google?.maps && cachedConfig) return Promise.resolve(cachedConfig);
  if (mapsLoader) return mapsLoader;
  mapsLoader = (async () => {
    try {
      const config = await firstValueFrom(http.get<GoogleMapsRuntimeConfig>(`${environment.apiUrl}/saas/google-maps-config`));
      if (!config?.apiKey) throw new Error('Google Maps no está configurado. Agrega la clave API en el backend.');
      await new Promise<void>((resolve, reject) => {
        const callback = `tusalaGoogleMapsReady_${Date.now()}`;
        (window as any)[callback] = () => {
          delete (window as any)[callback];
          resolve();
        };
        const script = document.createElement('script');
        // "places": autocompletado de direcciones mientras se escribe (el
        // "input autocomplete" pedido) -- antes solo se buscaba con el
        // Geocoder al presionar "Buscar" o Enter, sin ninguna sugerencia en
        // vivo. Ya estaba habilitado del lado de Google Cloud; faltaba
        // pedir esta librería y conectarla al campo de búsqueda.
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(config.apiKey)}&libraries=places&v=weekly&loading=async&callback=${callback}`;
        script.async = true;
        script.defer = true;
        script.onerror = () => reject(new Error('No fue posible cargar Google Maps.'));
        document.head.appendChild(script);
      });
      cachedConfig = config;
      return config;
    } catch (error) {
      // Sin esto, un fallo de red al pedir la config (backend caído, sin
      // conexión) dejaría mapsLoader apuntando para siempre a una promesa
      // ya rechazada -- todo intento posterior de abrir el selector
      // fallaría de inmediato sin volver a intentar el fetch.
      mapsLoader = null;
      throw error;
    }
  })();
  return mapsLoader;
}

@Component({
  selector: 'app-google-location-picker',
  standalone: true,
  imports: [CommonModule, FormsModule, InfoTipComponent],
  template: `
    <section class="location-picker" [class.frameless]="frameless" aria-labelledby="map-location-title">
      <div class="picker-heading">
        <strong id="map-location-title">Ubicación exacta *</strong>
        <app-info-tip label="¿Cómo marcar la ubicación?" text="Busca la dirección o haz clic en el mapa; luego arrastra el pin hasta la entrada del espacio. Esa es la ubicación que verán los clientes para llegar."></app-info-tip>
        <span class="confirmed" *ngIf="latitude !== null && longitude !== null"><i class="bi bi-geo-alt-fill" aria-hidden="true"></i> Pin colocado</span>
      </div>
      <div class="picker-tools">
        <label class="search">
          <i class="bi bi-search" aria-hidden="true"></i>
          <input #searchInput [(ngModel)]="searchText" [ngModelOptions]="{standalone:true}" (ngModelChange)="markSearchTouched()" (keydown.enter)="$event.preventDefault(); searchLocation()" placeholder="Buscar dirección o lugar" maxlength="255" autocomplete="off" aria-label="Buscar dirección o lugar" data-no-limit-hint />
        </label>
        <button type="button" class="primary" (click)="searchLocation()" [disabled]="loading || !searchText.trim()">Buscar</button>
        <span class="locate">
          <button type="button" class="secondary" (click)="useCurrentLocation()" [disabled]="loading"><i class="bi bi-crosshair" aria-hidden="true"></i> Usar mi ubicación</button>
          <app-info-tip label="¿Qué hace «Usar mi ubicación»?" text="Toma la ubicación actual de este dispositivo y coloca el pin ahí. Úsalo solo si estás dentro del espacio; si no, busca la dirección o mueve el pin."></app-info-tip>
        </span>
      </div>
      <div #map class="map" [class.unavailable]="fatalMapError"></div>
      <p class="picker-error" *ngIf="errorMessage">{{ errorMessage }}</p>
      <p class="coordinates" *ngIf="latitude !== null && longitude !== null">
        <i class="bi bi-pin-map" aria-hidden="true"></i>
        <span>{{ latitude | number:'1.5-7' }}, {{ longitude | number:'1.5-7' }}</span>
        <small>Arrastra el pin para ajustar.</small>
      </p>
    </section>
  `,
  styles: [`
    .location-picker{margin:1rem 0;padding:.9rem 1rem;border:1px solid var(--ts-border,#284c72);border-radius:12px;background:var(--ts-bg-elevated,#0a1c34);color:var(--ts-text)}
    .location-picker.frameless{margin:0;padding:0;border:0;background:transparent}
    .picker-heading{display:flex;align-items:center;gap:.35rem;margin-bottom:.6rem}
    .picker-heading strong{font-size:.8rem;font-weight:750;color:var(--ts-text-soft,#b7c6df)}
    .confirmed{margin-left:auto;display:inline-flex;align-items:center;gap:.3rem;padding:.12rem .55rem;border-radius:999px;color:var(--ts-text-green-350);background:var(--ts-bg-teal-550-a12);font-size:.72rem;font-weight:750}
    .picker-tools{display:grid;grid-template-columns:minmax(0,1fr) auto auto;align-items:center;gap:.5rem;margin-bottom:.65rem}
    .search{position:relative;display:block;min-width:0}
    .search i{position:absolute;left:.75rem;top:50%;transform:translateY(-50%);color:var(--ts-text-muted,#91a4bf);font-size:.85rem;pointer-events:none}
    .location-picker .search input{width:100%;box-sizing:border-box;height:40px;min-height:40px!important;padding:0 .75rem 0 2.15rem!important;border:1px solid var(--ts-border-strong,#3a628e);border-radius:10px;color:var(--ts-text);background:var(--ts-input-bg,#0a213b);font:inherit;font-size:.86rem}
    .search input::placeholder{color:var(--ts-text-muted,#91a9c4)}
    .search input:focus{outline:none;border-color:var(--ts-primary,#2f7cff)}
    .picker-tools button{display:inline-flex;align-items:center;justify-content:center;gap:.4rem;height:40px;min-height:40px;padding:0 .9rem;border:1px solid transparent;border-radius:10px;font:inherit;font-size:.82rem;font-weight:700;white-space:nowrap;cursor:pointer}
    .picker-tools button.primary{color:var(--ts-text-on-accent);background:linear-gradient(135deg,var(--ts-bg-blue-500),var(--ts-bg-sky-450))}
    .picker-tools button.secondary{color:var(--ts-text);border-color:var(--ts-border-strong,#3a628e);background:var(--ts-surface,#123b67)}
    .picker-tools button:hover:not(:disabled){filter:brightness(1.1)}
    .picker-tools button:disabled{opacity:.48;cursor:not-allowed}
    .locate{display:inline-flex;align-items:center;gap:.15rem}
    .map{height:320px;border:1px solid var(--ts-border,#42688f);border-radius:11px;background:var(--ts-surface,#102b49);overflow:hidden}
    .map.unavailable{height:90px}
    .picker-error{margin:.6rem 0 0;padding:.55rem .7rem;border:1px solid var(--ts-border-red-600-2);border-radius:8px;color:var(--ts-text-red-100-6);background:var(--ts-bg-red-800-3);font-size:.78rem}
    .coordinates{display:flex;align-items:center;flex-wrap:wrap;gap:.35rem .6rem;margin:.55rem 0 0;color:var(--ts-text-soft,#b8cce3);font-size:.76rem;font-variant-numeric:tabular-nums}
    .coordinates i{color:var(--ts-text-blue-250-3)}
    .coordinates small{color:var(--ts-text-muted,#91a4bf)}
    @media(max-width:680px){.picker-tools{grid-template-columns:minmax(0,1fr) auto}.search{grid-column:1/-1}.locate{justify-self:end}.map{height:260px}}
  `]
})
export class GoogleLocationPickerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('map', { static: true }) mapElement!: ElementRef<HTMLDivElement>;
  @ViewChild('searchInput', { static: true }) searchInputElement!: ElementRef<HTMLInputElement>;
  @Input() latitude: number | null = null;
  @Input() longitude: number | null = null;
  @Input() set address(value: string) {
    if (!this.searchTouched) this.searchText = value || '';
  }
  @Output() locationChange = new EventEmitter<SpaceMapLocation>();
  /** Sin marco propio (cuando ya va dentro de una tarjeta): evita el doble contorno. */
  @Input() frameless = false;

  searchText = '';
  loading = false;
  errorMessage = '';
  fatalMapError = false;
  private searchTouched = false;
  private notifications = inject(NotificationService);
  private http = inject(HttpClient);
  private map: any;
  private marker: any;
  private geocoder: any;
  private autocomplete: any;
  private mapClickListener: any;
  private placeChangedListener: any;

  async ngAfterViewInit(): Promise<void> {
    try {
      await loadGoogleMaps(this.http);
      const center = this.hasCoordinates() ? { lat: Number(this.latitude), lng: Number(this.longitude) } : { lat: 13.6929, lng: -89.2182 };
      this.map = new google.maps.Map(this.mapElement.nativeElement, { center, zoom: this.hasCoordinates() ? 17 : 12, streetViewControl: false, mapTypeControl: false });
      this.geocoder = new google.maps.Geocoder();
      if (this.hasCoordinates()) this.setMarker(center, false);
      this.mapClickListener = this.map.addListener('click', (event: any) => this.setMarker({ lat: event.latLng.lat(), lng: event.latLng.lng() }, true));
      this.initAutocomplete();
    } catch (error: any) {
      this.fatalMapError = true;
      this.errorMessage = error?.message || 'No fue posible iniciar el selector de ubicación.';
      this.notifications.error(this.errorMessage);
    }
  }

  ngOnDestroy(): void {
    if (this.mapClickListener) (window as any).google?.maps?.event?.removeListener(this.mapClickListener);
    if (this.placeChangedListener) (window as any).google?.maps?.event?.removeListener(this.placeChangedListener);
  }

  // Sugerencias de dirección en vivo mientras se escribe (Google Places
  // Autocomplete), acotadas a El Salvador -- mismo sesgo regional que ya
  // usaba el Geocoder manual (region:'sv'). Elegir una sugerencia posiciona
  // el mapa y el pin de inmediato, sin tener que presionar "Buscar".
  private initAutocomplete(): void {
    this.autocomplete = new google.maps.places.Autocomplete(this.searchInputElement.nativeElement, {
      componentRestrictions: { country: 'sv' },
      fields: ['geometry', 'formatted_address', 'place_id'],
    });
    this.placeChangedListener = this.autocomplete.addListener('place_changed', () => {
      const place = this.autocomplete.getPlace();
      const location = place?.geometry?.location;
      if (!location) return;
      this.searchTouched = true;
      this.errorMessage = '';
      // Google escribe la dirección elegida directo en el <input> del DOM,
      // sin pasar por Angular -- se resincroniza searchText para que un
      // "Buscar" posterior (sin volver a escribir) use la dirección real
      // que quedó en pantalla, no un valor de ngModel desactualizado.
      this.searchText = this.searchInputElement.nativeElement.value;
      const position = { lat: location.lat(), lng: location.lng() };
      this.map.setCenter(position); this.map.setZoom(17);
      this.setMarker(position, true, place.place_id ?? null, place.formatted_address ?? this.searchText);
    });
  }

  markSearchTouched(): void {
    this.searchTouched = true;
  }

  async searchLocation(): Promise<void> {
    if (!this.geocoder || !this.searchText.trim()) return;
    this.searchTouched = true;
    this.loading = true; this.errorMessage = '';
    try {
      const response = await this.geocoder.geocode({ address: this.searchText.trim(), region: 'sv' });
      const result = response.results?.[0];
      if (!result) throw new Error('No encontramos esa ubicación. Intenta con otra referencia.');
      const position = { lat: result.geometry.location.lat(), lng: result.geometry.location.lng() };
      this.map.setCenter(position); this.map.setZoom(17);
      this.setMarker(position, true, result.place_id, result.formatted_address);
    } catch (error: any) {
      this.errorMessage = error?.message || 'No fue posible buscar la ubicación.';
      this.notifications.error(this.errorMessage);
    } finally { this.loading = false; }
  }

  useCurrentLocation(): void {
    if (!navigator.geolocation) {
      this.errorMessage = 'Este navegador no permite obtener la ubicación.';
      this.notifications.error(this.errorMessage);
      return;
    }
    this.loading = true; this.errorMessage = '';
    navigator.geolocation.getCurrentPosition(position => {
      const coordinates = { lat: position.coords.latitude, lng: position.coords.longitude };
      this.map?.setCenter(coordinates); this.map?.setZoom(18); this.setMarker(coordinates, true); this.loading = false;
    }, (error) => {
      this.loading = false;
      this.errorMessage = error.code === error.PERMISSION_DENIED
        ? 'El navegador tiene bloqueado el permiso de ubicación. Puedes buscar una dirección o colocar el pin manualmente.'
        : 'No fue posible detectar tu ubicación. Puedes buscar una dirección o colocar el pin manualmente.';
      this.notifications.error(this.errorMessage);
    }, { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 });
  }

  private hasCoordinates(): boolean {
    return this.latitude !== null && this.longitude !== null
      && Number.isFinite(Number(this.latitude)) && Number.isFinite(Number(this.longitude));
  }

  private setMarker(position: { lat: number; lng: number }, emit: boolean, placeId: string | null = null, formattedAddress: string | null = null): void {
    if (!this.map) return;
    this.errorMessage = '';
    if (!this.marker) {
      this.marker = new google.maps.Marker({ map: this.map, position, draggable: true, title: 'Ubicación exacta del espacio' });
      this.marker.addListener('dragend', () => {
        const next = this.marker.getPosition();
        this.publish(next.lat(), next.lng());
      });
    } else this.marker.setPosition(position);
    if (emit) this.publish(position.lat, position.lng, placeId, formattedAddress);
  }

  private publish(latitude: number, longitude: number, google_place_id: string | null = null, formatted_address: string | null = null): void {
    this.latitude = latitude; this.longitude = longitude;
    this.locationChange.emit({ latitude, longitude, google_place_id, formatted_address });
  }
}
